import { ScrollPanel } from "primereact/scrollpanel";
import React, { useEffect, useRef, useState } from "react";
import "./Docupload.styles.css";
import uploadImg from "../../assets/images/uploadicon.svg";
import { ImageConfig } from "../../utils/ImageConfig";
import { Card, Col, Row } from "react-bootstrap";
import UploadFileIcon from "../../assets/images/uploadFileIcon.svg";
import moreIcon from "../../assets/images/moreIcon.svg";
import { uploadCARFiles, getFilesByFolderId } from "../../services/upload.api";
import Toaster from "../Toaster/Toster";
import TickIcon from "../../assets/images/tickIcon.svg"

interface DropFileInputProps {
  onFileChange: (files: File[]) => void;
  folderPath: string;
  disabled: any;
}
type DisplayFile =
  | { type: "uploaded"; file: File; name: string }
  | { type: "existing"; name: string };

const Docupload: React.FC<DropFileInputProps> = ({ onFileChange, disabled, folderPath }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [fileList, setFileList] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<string[]>([]);
  const [show, setShow] = useState<boolean>(false);
  const [uploadToast, setUploadToast] = useState<{ type: "success" | "error" | "warning", message: string }>({ type: "success", message: "" });
  const [isDocSuccess, setIsDocSuccess] = useState<boolean>(false);
  const [isDocFail, setIsDocFail] = useState<boolean>(false);

  const onDragEnter = () => wrapperRef.current?.classList.add("dragover");
  const onDragLeave = () => wrapperRef.current?.classList.remove("dragover");
  const onDrop = () => wrapperRef.current?.classList.remove("dragover");

  const fetchFiles = async () => {
    console.log("fetchFiles", folderPath);

    try {
      const response = await getFilesByFolderId(folderPath);

      // Access the `files` array correctly from the response object
      if (response && Array.isArray(response.files)) {
        setExistingFiles(response.files); // Update state with the `files` array
      } else {
        console.error("Invalid response structure for existing files", response);
        setExistingFiles([]); // fallback to empty array
      }
    } catch (err) {
      console.error("Failed to fetch existing files:", err);
      setExistingFiles([]); // fallback to empty array in case of error
    }
  };



  const onFileDrop = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (folderPath) {

      const newFiles = Array.from(e.target.files || []);
      newFiles.forEach((file) => console.log(file));
      // all files should be in valid format i.e .pdf,.txt,.doc,.xls
      const validFileTypes = ["application/pdf", "text/plain", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];
      const validFiles = newFiles.filter((file) => validFileTypes.includes(file.type));
      if (validFiles.length !== newFiles.length) {
        setUploadToast({ type: "error", message: `The file type you uploaded is not supported, please upload a .pfd, .xls, .txt or .docx.` });
        setShow(true);
        setIsDocFail(true);
        setTimeout(() => {
          setIsDocFail(false);
        }, 4000)
        return;    
      } else if (newFiles.every(file => file.size > 10 * 1024 * 1024 + 5)) {
        setUploadToast({ type: "error", message: `This file exceeds the max allowed file size. Please upload a file that does not exceed 10mb.` });
        setShow(true);
        setIsDocFail(true);
        setTimeout(() => {
          setIsDocFail(false);
        }, 4000)
      } else {
        for (const file of newFiles) {
          try {
            const response = await uploadCARFiles(file, folderPath);
            setIsDocSuccess(true);
            const updatedList = [...fileList, ...validFiles];
            setFileList(updatedList);
            onFileChange(updatedList);
            console.log("Upload successful:", response);
            setShow(true);
            setUploadToast({ type: "success", message: `File uploaded successfully.` });
            setTimeout(() => {
              setIsDocSuccess(false);
            }, 4000)
          } catch (err) {
            setIsDocFail(true);
            console.error("Upload failed:", err);
            setShow(true);
            setUploadToast({ type: "error", message: `It seems there was an error on our end preventing your upload from completing, please try uploading the document again.` });
            setTimeout(() => {
              setIsDocFail(false);
            }, 4000)
          }
        }

        // Refresh file list after upload
        fetchFiles();
      }

    } else {
      setUploadToast({ type: "error", message: `Something went wrong on our end, please try uploading your document again.` });
      setShow(true)
    }


  };

  const fileRemove = (file: File) => {
    const updatedList = fileList.filter((item) => item !== file);
    setFileList(updatedList);
    onFileChange(updatedList);
  };

  const downloadFile = (file: File) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchFiles();
  }, [folderPath]);

  const allFiles: DisplayFile[] = [
    ...existingFiles.map((filename) => ({ name: filename, type: "existing" as const })),
    ...fileList.map((file) => ({ name: file.name, type: "uploaded" as const, file })),
  ];


  // Optional: remove duplicates by file name
  const uniqueFiles = Array.from(new Map(allFiles.map(item => [item.name, item])).values());

  return (
    <>
      {!isDocSuccess && !isDocFail ?
        <div
          ref={wrapperRef}
          className="p-4 text-center my-4 upload-document-input"
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          style={{
            position: "relative",
            display: "inline-block",
            cursor: "pointer",
          }}
        >
          <div className="d-flex align-items-center justify-content-center">
            <img src={uploadImg} alt="Upload" style={{ marginRight: "10px" }} />
            <div className="text-start">
              <h2 className="upload-document-label m-0">
                Select documents to upload or drag & drop
              </h2>
              <span className="upload-document-sublabel text-start">
                Supported file types: pdf, txt, doc, xls
              </span>
            </div>
          </div>
          <input
            type="file"
            accept=".pdf,.txt,.doc,.xls"
            onChange={onFileDrop}
            multiple
            style={{
              opacity: 0,
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              cursor: disabled ? "not-allowed" : "pointer",
            }}
            disabled={disabled}
          />
        </div> : <></>}

      {isDocSuccess &&
        <div className="upload-document-input">
          <div className="doc-status-content">
            <svg width="100" height="100">
              <circle
                fill="none"
                stroke="#78A22F"
                stroke-width="5"
                cx="50"
                cy="50"
                r="45"
                strokeLinecap="round"
                transform="rotate(-40 50 50)"
                className="doc-status-circle"
              />
              <polyline
                fill="none"
                stroke="#78A22F"
                points="20,53 45,73 80,36"
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="doc-status-tick"
              />
            </svg>
            <p className="doc-status-tick-text">Document Uploaded Successfully.</p>
          </div>
        </div>
      }

      {isDocFail &&
        <div className="upload-document-input">
          <div className="doc-status-content">
            <svg width="100" height="100">
              <circle
                fill="none"
                stroke="#C43131"
                stroke-width="5"
                cx="50"
                cy="50"
                r="45"
                strokeLinecap="round"
                transform="rotate(-40 50 50)"
                className="doc-status-circle"
              />
              <path
                fill="none"
                stroke="#C43131"
                stroke-width="6"
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M 25,25 L 75,75 M 75,25 L 25,75"
                className="doc-status-cross"
              />
            </svg>
            <p className="doc-status-cross-text">Document Uploaded Failed, Please Try Again.</p>
          </div>
        </div>
      }

      {uniqueFiles && uniqueFiles.length > 0 ? (
        <div className="uploaded-file-container">
          <Row className="g-3">
            {uniqueFiles.map((item, index) => (
              <Col xs={12} md={6} xl={6} xxl={6} sm={12} key={index}>
                <Card className="d-flex flex-row align-items-top p-2 justify-content-between uploadfile">
                  <div className="d-flex">
                    <img src={UploadFileIcon} alt="File" />
                    <div className=" mx-2">
                      <p className="mb-0 file-name">{item.name}</p>
                      <small className="file-size">
                        {item.type === "uploaded" ? `${item.file?.size} kb` : "Uploaded"}
                      </small>
                    </div>
                  </div>
                  {/* <div style={{ marginRight: "10px", cursor: "pointer" }}>
                  {item.type === "uploaded" ? (
                    <img src={moreIcon} onClick={() => downloadFile(item.file)} />
                  ) : (
                    <img src={downloadIcon} title="Download from server" />
                  )}
                </div> */}
                  <img className='doc-success-tick-icon' src={TickIcon} alt={''} />
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      ) : (
        <>
          <div className='file-upload-empty'>
            <p className='file-upload-main-empty'>There are no uploaded files.</p>
            {!folderPath &&
              <p className='file-upload-sub-empty'>File Upload Path Not Available.</p>
            }
          </div>

        </>
      )}
      <Toaster
        type={uploadToast.type}
        show={show}
        setShow={setShow}
        message={uploadToast.message}
      />
    </>
  );
};

export default Docupload;
